/**
 * East Vibe homepage Cloudinary hero — deferred video after poster LCP.
 * State: poster-visible → critical-page-ready → video-source-attached →
 * video-buffering → video-playing → poster-crossfaded-out
 */
(function () {
  'use strict';

  var NS = 'evCloudinaryHero';
  var IDLE_TIMEOUT_MS = 1800;
  var FONT_TIMEOUT_MS = 1200;
  var PLAY_TIMEOUT_MS = 12000;
  var instances = new WeakMap();

  function isMobileViewport() {
    try {
      if (window.matchMedia && window.matchMedia('(max-width: 767px)').matches) {
        return true;
      }
    } catch (e) {
      if (Math.min(window.innerWidth || 0, document.documentElement.clientWidth || 0) <= 767) {
        return true;
      }
    }
    return false;
  }

  function isSlowConnection() {
    try {
      var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (!conn) return false;
      if (conn.saveData) return true;
      var type = String(conn.effectiveType || '').toLowerCase();
      return type === 'slow-2g' || type === '2g';
    } catch (e) {
      return false;
    }
  }

  function prefersPosterOnly(root) {
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return true;
      }
    } catch (e) {}
    if (isSlowConnection()) {
      return true;
    }
    if (isMobileViewport()) {
      var allowMobile = !!(root && (
        root.getAttribute('data-ev-allow-mobile-video') === 'true' ||
        root.getAttribute('data-ev-video-src-mobile')
      ));
      if (!allowMobile) {
        return true;
      }
    }
    return false;
  }

  function resolveVideoSrc(root) {
    if (!root) return '';
    if (isMobileViewport()) {
      return root.getAttribute('data-ev-video-src-mobile') || '';
    }
    return root.getAttribute('data-ev-video-src') || '';
  }

  function setState(root, state) {
    if (!root) return;
    root.setAttribute('data-ev-state', state);
  }

  function whenPosterSettled(img) {
    return new Promise(function (resolve) {
      if (!img) {
        resolve();
        return;
      }
      if (img.complete) {
        resolve();
        return;
      }
      var done = function () {
        img.removeEventListener('load', done);
        img.removeEventListener('error', done);
        resolve();
      };
      img.addEventListener('load', done);
      img.addEventListener('error', done);
    });
  }

  function whenFontsSettled() {
    return new Promise(function (resolve) {
      var settled = false;
      var finish = function () {
        if (settled) return;
        settled = true;
        resolve();
      };
      if (!document.fonts || !document.fonts.ready) {
        finish();
        return;
      }
      var timer = setTimeout(finish, FONT_TIMEOUT_MS);
      document.fonts.ready.then(function () {
        clearTimeout(timer);
        finish();
      }, finish);
    });
  }

  function whenLoaderOrLoad() {
    return new Promise(function (resolve) {
      var settled = false;
      var finish = function () {
        if (settled) return;
        settled = true;
        resolve();
      };

      var loader = document.getElementById('pre-loading');
      if (!loader || !loader.classList.contains('loading-page')) {
        finish();
        return;
      }

      var observer = new MutationObserver(function () {
        if (!loader.classList.contains('loading-page')) {
          observer.disconnect();
          finish();
        }
      });
      observer.observe(loader, { attributes: true, attributeFilter: ['class'] });

      if (document.readyState === 'complete') {
        finish();
      } else {
        window.addEventListener('load', finish, { once: true });
      }

      setTimeout(finish, 2500);
    });
  }

  function whenIdle() {
    return new Promise(function (resolve) {
      var settled = false;
      var finish = function () {
        if (settled) return;
        settled = true;
        resolve();
      };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(finish, { timeout: IDLE_TIMEOUT_MS });
        setTimeout(finish, IDLE_TIMEOUT_MS + 50);
      } else {
        setTimeout(finish, Math.min(IDLE_TIMEOUT_MS, 400));
      }
    });
  }

  function cleanupVideo(inst) {
    if (!inst || !inst.video) return;
    var video = inst.video;
    try { video.pause(); } catch (e) {}
    video.removeAttribute('src');
    while (video.firstChild) {
      video.removeChild(video.firstChild);
    }
    try { video.load(); } catch (e2) {}
    if (inst.onPlaying) video.removeEventListener('playing', inst.onPlaying);
    if (inst.onError) video.removeEventListener('error', inst.onError);
    if (inst.onCanPlay) video.removeEventListener('canplay', inst.onCanPlay);
    if (inst.playTimer) {
      clearTimeout(inst.playTimer);
      inst.playTimer = null;
    }
  }

  function failToPoster(inst) {
    if (!inst || inst.failed) return;
    inst.failed = true;
    cleanupVideo(inst);
    setState(inst.root, 'poster-visible');
    inst.root.classList.remove('is-video-playing');
  }

  function attachAndPlay(inst) {
    if (!inst || inst.started || inst.destroyed || inst.failed) return;
    if (prefersPosterOnly(inst.root)) {
      setState(inst.root, 'poster-visible');
      return;
    }

    var video = inst.video;
    var src = resolveVideoSrc(inst.root);
    if (!video || !src) {
      setState(inst.root, 'poster-visible');
      return;
    }

    inst.started = true;
    setState(inst.root, 'video-source-attached');

    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.loop = true;
    video.setAttribute('src', src);
    setState(inst.root, 'video-buffering');

    inst.onPlaying = function () {
      if (inst.destroyed || inst.failed) return;
      if (inst.playTimer) {
        clearTimeout(inst.playTimer);
        inst.playTimer = null;
      }
      setState(inst.root, 'video-playing');
      requestAnimationFrame(function () {
        inst.root.classList.add('is-video-playing');
        setState(inst.root, 'poster-crossfaded-out');
      });
    };

    inst.onError = function () {
      failToPoster(inst);
    };

    inst.onCanPlay = function () {
      if (inst.destroyed || inst.failed || inst.playAttempted) return;
      inst.playAttempted = true;
      var playPromise = video.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(function () {
          failToPoster(inst);
        });
      }
    };

    video.addEventListener('playing', inst.onPlaying);
    video.addEventListener('error', inst.onError);
    video.addEventListener('canplay', inst.onCanPlay);

    inst.playTimer = setTimeout(function () {
      if (!inst.root.classList.contains('is-video-playing')) {
        failToPoster(inst);
      }
    }, PLAY_TIMEOUT_MS);

    try {
      video.load();
    } catch (e) {
      failToPoster(inst);
      return;
    }

    if (video.readyState >= 3) {
      inst.onCanPlay();
    }
  }

  function destroy(root) {
    var inst = instances.get(root);
    if (!inst) return;
    inst.destroyed = true;
    cleanupVideo(inst);
    instances.delete(root);
  }

  function initRoot(root) {
    if (!root || instances.has(root)) return;
    if (!root.getAttribute('data-ev-video-src')) return;

    var poster = root.querySelector('[data-ev-hero-poster]');
    var video = root.querySelector('[data-ev-hero-video]');
    if (!video) return;

    var inst = {
      root: root,
      poster: poster,
      video: video,
      started: false,
      playAttempted: false,
      failed: false,
      destroyed: false,
      playTimer: null,
      onPlaying: null,
      onError: null,
      onCanPlay: null
    };
    instances.set(root, inst);
    setState(root, 'poster-visible');

    if (prefersPosterOnly(root)) {
      return;
    }

    Promise.all([
      whenPosterSettled(poster),
      whenFontsSettled(),
      whenLoaderOrLoad()
    ]).then(function () {
      if (inst.destroyed) return;
      setState(root, 'critical-page-ready');
      return whenIdle();
    }).then(function () {
      if (inst.destroyed) return;
      attachAndPlay(inst);
    });
  }

  function initAll(scope) {
    var root = scope || document;
    var nodes = root.querySelectorAll
      ? root.querySelectorAll('[data-ev-cloudinary-hero]')
      : [];
    for (var i = 0; i < nodes.length; i++) {
      initRoot(nodes[i]);
    }
  }

  function onPageHide() {
    var nodes = document.querySelectorAll('[data-ev-cloudinary-hero]');
    for (var i = 0; i < nodes.length; i++) {
      var inst = instances.get(nodes[i]);
      if (!inst || !inst.video) continue;
      try { inst.video.pause(); } catch (e) {}
    }
  }

  function onPageShow(event) {
    if (event && event.persisted) {
      var nodes = document.querySelectorAll('[data-ev-cloudinary-hero]');
      for (var i = 0; i < nodes.length; i++) {
        var root = nodes[i];
        var inst = instances.get(root);
        if (prefersPosterOnly(root)) {
          if (inst) failToPoster(inst);
          continue;
        }
        if (inst && inst.root.classList.contains('is-video-playing') && inst.video) {
          try { inst.video.play(); } catch (e) {}
        }
      }
    }
  }

  function bindGlobal() {
    if (window[NS + 'Bound']) return;
    window[NS + 'Bound'] = true;

    document.addEventListener('shopify:section:load', function (event) {
      var section = event.target;
      if (!section) return;
      initAll(section);
    });

    document.addEventListener('shopify:section:unload', function (event) {
      var section = event.target;
      if (!section || !section.querySelectorAll) return;
      var nodes = section.querySelectorAll('[data-ev-cloudinary-hero]');
      for (var i = 0; i < nodes.length; i++) {
        destroy(nodes[i]);
      }
    });

    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
  }

  function boot() {
    bindGlobal();
    initAll(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.evCloudinaryHero = {
    init: initAll,
    destroy: destroy
  };
})();
